// The parts of a cohort_explorer cohort state that the chat card and the
// sidebar show. Counts arrive already masked for the viewer by the service.
export interface CohortCriterionSummary {
  label: string;
  participantCount: number | null;
}

export interface CohortSummary {
  name: string;
  participantCount: number | null;
  // True while the service is still calculating the counts.
  pending: boolean;
  startingCount: number | null;
  criteria: CohortCriterionSummary[];
}

function asCount(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function criterionLabel(criterion: Record<string, unknown>, index: number): string {
  for (const field of ["llm_description", "user_description"]) {
    const value = criterion[field];
    if (typeof value === "string" && value.trim()) return value;
  }
  return `Criterion ${index + 1}`;
}

/** Reads a cohort state object (as matched by the pane) into a display summary. */
export function summarizeCohort(state: Record<string, unknown>): CohortSummary {
  const criteria = Array.isArray(state.criteria) ? state.criteria : [];
  return {
    name: typeof state.name === "string" && state.name ? state.name : "Untitled cohort",
    participantCount: asCount(state.participant_count),
    pending: state.pending_count === true,
    startingCount: asCount(state.starting_participant_count),
    criteria: criteria
      .filter((c): c is Record<string, unknown> => c != null && typeof c === "object")
      .map((c, index) => ({
        label: criterionLabel(c, index),
        participantCount: asCount(c.participant_count),
      })),
  };
}

/** "1,234 participants", "Calculating…" while pending, or null with no count. */
export function formatCohortCount(summary: CohortSummary, refreshFailed = false): string | null {
  if (summary.pending) return refreshFailed ? "Count unavailable" : "Calculating…";
  if (summary.participantCount == null) return null;
  const noun = summary.participantCount === 1 ? "participant" : "participants";
  return `${summary.participantCount.toLocaleString()} ${noun}`;
}
